import { trimTrailingZeros } from '@utils/formatting-utils';

export default class FormattingService {
  public static formatEnumToBackend(value: string): string {
    return value
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  /**
   *
   * @param {string} value - The value to be formatted
   * @returns - The formatted value as a string with the first letter of each word capitalized and joined by spaces.
   *
   * @example
   * // Returns 'Non Proportional'
   * formatEnum('NON_PROPORTIONAL');
   *
   * @example
   * // Returns 'Property'
   * formatEnum('PROPERTY');
   */
  public static formatEnumToDisplay(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  public static formatCustomCurrency(
    numberValue: number,
    customCurrencySymbol?: string,
    decimals = 2,
    shouldTrimTrailingZeros = false
  ): string {
    const stringValue = numberValue.toString().replace(/,/g, '');
    const [integerPart, decimalPart = ''] = stringValue.split('.');
    const formattedIntegerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ','
    );
    let formattedNumber = `${formattedIntegerPart}${
      decimalPart ? `.${decimalPart}` : ''
    }`;

    if (shouldTrimTrailingZeros) {
      formattedNumber =
        trimTrailingZeros(formattedNumber, decimals, true).toString() || '0';
    }
    // Manually append the custom currency symbol
    return `${customCurrencySymbol} ${formattedNumber}`.trim();
  }

  /**
   * Converts a camel- or Pascal-cased string (e.g. "RequestedByInsured")
   * into an UPPER_SNAKE_CASE string (e.g. "REQUESTED_BY_INSURED").
   *
   * @param {string} value - The string to convert.
   * @returns {string} The resulting UPPER_SNAKE_CASE string.
   */
  public static toUpperSnakeCase(value: string): string {
    if (!value) return '';

    // Insert underscores before uppercase letters, then remove any leading underscore,
    // and finally convert to uppercase.
    const snakeCased = value.replace(/([A-Z])/g, '_$1').replace(/^_/, '');
    return snakeCased.toUpperCase();
  }

  /**
   * Converts a camelCase or PascalCase string into a space-separated string
   * with each word capitalized.
   *
   * @param {string} value - The camelCase or PascalCase string to convert.
   * @returns {string} The resulting string with spaces.
   *
   * @example
   * // Returns 'Requested By Insured'
   * formatCamelCaseToDisplay('RequestedByInsured');
   */
  public static formatCamelCaseToDisplay(value: string): string {
    if (!value) return '';
    // This regex finds uppercase letters that are not at the beginning and adds a space before them.
    return value.replace(/(?!^)([A-Z])/g, ' $1');
  }
}
